import { FC, useState, useEffect } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import {
	Alert,
	Box,
	Button,
	ButtonDropdown,
	ColumnLayout,
	Container,
	ContentLayout,
	Flashbar,
	Grid,
	Header,
	SpaceBetween
} from '@cloudscape-design/components'

import ApprovalDetails from '../../../components/Application/ApprovalDetails'
import ApprovalForm from '../../../components/Application/ApprovalForm'
import RejectionForm from '../../../components/Application/RejectionForm'
import LoadingSpinner from '../../../components/LoadingSpinner'
import CONSTANTS from '../../../constants'
import useSingleApplication from '../../../hooks/query/useSingleApplication'
import RequiredPermissions from '../../../helper/RequiredPermissions'
import CommentsOverview from '../../../components/Application/CommentsOverview'
import RenderApplicationFields from './RenderApplicationFields'
import useUserPermissions from '../../../hooks/query/useUserPermissions'
import HeaderWithStatusBadges from '../../../components/Common/HeaderWithStatusBadges'
import AccessDetails from '../../../components/Application/AccessDetails'
import ReadGenericTags from '../../../components/ControlledFormInputs/GenericTagsReadGCP'
import EditGenericTags from '../../../components/ControlledFormInputs/GenericTagsEditGCPApplication'
import ProjectResourcePanel from '../../../components/ProjectResource/ProjectResourcesPanel'
import { useFlashbarStateStore } from '../../../stores/notifications/flashbarStateStore'
import { areApplicationTagsApproved } from '../../../helper/applicationTagApproval'
import type { TagsApproval } from '../../../types/tags'
import hasAllPermissions from '../../../helper/hasRequiredPermissions'
import getPlatformOwnerPermissions from '../../../helper/platformOwnerPermissions'
import { isMongoDBApplicationEligible } from '../../../helper/mongodbApplicationEligibility'
import CodeBrokkrModal from '../../../components/CodeBrokkr/CodeBrokkrIFrame/CodeBrokkrModal'

const ApplicationDetails: FC = () => {
	const CURRENT_USER_ID = JSON.parse(localStorage['okta-token-storage'])[
		'idToken'
	]['claims']['email']
	const { id } = useParams() as { id: string }
	const location = useLocation()
	const { data: thisApplication, isLoading } = useSingleApplication(id)
	const thisApplicationID = thisApplication?.applicationID || ''
	const {
		data: userPermissions,
	} = useUserPermissions(CURRENT_USER_ID)
	const hasSpecificPermissions = (
		permissions: string[],
		requiredPermissions: string[]
	): boolean => {
		return requiredPermissions.some((requiredPerm) =>
			permissions.includes(requiredPerm)
		)
	}
	const specificPermissions = [
		'applications:*:*',
		'applications:*:approve',
		'*:*:*'
	]
	const hasRequiredPermissions =
		userPermissions &&
		hasSpecificPermissions(userPermissions.permissions, specificPermissions)
	const platformOwnerResource = {
		type: 'applications' as const,
		id: thisApplicationID
	}
	const hasPlatformOwnerPermissions = Boolean(
		userPermissions &&
		hasAllPermissions(
			getPlatformOwnerPermissions(platformOwnerResource),
			userPermissions.permissions
		)
	)
	const canReadApplicationTags = Boolean(
		userPermissions &&
		(hasAllPermissions(
			CONSTANTS.APPLICATION_REQUESTOR,
			userPermissions.permissions
		) || hasPlatformOwnerPermissions)
	)
	const [showApprovalForm, setShowApprovalForm] = useState(false)
	const [showRejectionForm, setShowRejectionForm] = useState(false)
  const [showButton, setShowButton] = useState(true)
	const [isCodeBrokkrModalOpen, setIsCodeBrokkrModalOpen] = useState(false)

	// const [flag, setFlag] = useState(false)

	const {items: flashbarItems, removeItem} = useFlashbarStateStore()

	useEffect(() => {
	  return () => {
      flashbarItems.forEach(item => (item.id.split(':')[1] === `${location.pathname}ca/configure`) ? console.log(item.id.split(':')[1]) : removeItem(item.id))
		}
	}, [location, flashbarItems, removeItem])

	if (!thisApplication || isLoading) {
		return (
			<ContentLayout
				header={
					<HeaderWithStatusBadges
						headerName=""
						isLoadingApprovals
						skeletonBadgeCount={5}
						navigateTo="/applications"
					/>
				}
			>
				<LoadingSpinner />
			</ContentLayout>
		)
	}

	if (thisApplication.status === 'ERROR') {
		return (
			<Alert
				statusIconAriaLabel="Error"
				type="error"
				header="An error occurred"
			>
				{thisApplication.details.message}
			</Alert>
		)
	}

	const applicationVersionedObject = getKeyValueApplication(thisApplication)

	function handleShowAccessDetails(): void {
		setShowButton(!showButton)
	}

	const projectResourcePanelItems = [
		{
			relativePath: 'gcpCerts/new',
			description: 'Request GCP Certificate'
		},
		{
			relativePath: 'ca/configure',
			description: 'Configure Connectivity Analyzer'
		},
		...(isMongoDBApplicationEligible(thisApplication)
			? [
				{
					relativePath: 'mongodb/new',
					description: 'Onboard MongoDB'
				}
			] : [])
	]

	const applicationTagApprovals: TagsApproval[] =
		thisApplication.tags?.approvalStatus || []
	// Scoped to this application: the tag record is shared with the GCP Project and with
	// every other application under it, so an unfiltered check would let any neighbour's
	// pending approval block this request.
	const thisApplicationName = thisApplication?.details?.applicationName || ''
	const isAllTagsApproved = areApplicationTagsApproved(
		applicationTagApprovals,
		thisApplicationName
	)

	return (
		<ContentLayout
			header={
				<HeaderWithStatusBadges
					headerName={thisApplication?.details.applicationName}
					status={thisApplication?.status}
					approvalStatus={applicationTagApprovals}
					includeCloudTribe={true}
					navigateTo="/applications"
				/>
			}
		>
			<Grid
				gridDefinition={[
					{ colspan: { default: 3, xxs: 9 } },
					{ colspan: { default: 9, xxs: 3 } }
				]}
			>
				<SpaceBetween direction="vertical" size="l">
				  {(flashbarItems.length > 0) && <Flashbar items={flashbarItems}/>}
					<RenderApplicationFields Application={applicationVersionedObject} />

					{!hasRequiredPermissions &&
						thisApplication.status === 'PENDING_APPROVAL' &&
						canReadApplicationTags && (
							<Container header={<Header variant="h3">Tag Details</Header>}>
								{thisApplication.tagsID && (
									<ReadGenericTags
										tagsId={thisApplication.tagsID}
										applicationName={thisApplicationName}
										platformOwnerResource={platformOwnerResource}
										allowPlatformOwnerApproval={true}
										platformOwnerEmail={
											thisApplication?.details?.platformOwnerEmail
										}
										applicationOwnerEmail={
											thisApplication?.details?.applicationOwnerEmail
										}
									/>
								)}
							</Container>
						)}

					{(thisApplication.status === 'APPROVED' ||
						thisApplication.status === 'REJECTED') && (
						<RequiredPermissions
							requiredPermissions={CONSTANTS.APPLICATION_REQUESTOR}
						>
							<Container header={<Header variant="h3">Tag Details</Header>}>
								{thisApplication.tagsID && (
									<ReadGenericTags tagsId={thisApplication.tagsID} />
								)}
							</Container>
						</RequiredPermissions>
					)}

					{thisApplication.status === 'PENDING_APPROVAL' &&
						hasRequiredPermissions && (
							<RequiredPermissions
								requiredPermissions={CONSTANTS.APPLICATION_APPROVER}
							>
								<Container header={<Header variant="h3">Tag Details</Header>}>
									{thisApplication.tagsID && (
										<EditGenericTags
											tagsId={thisApplication.tagsID}
											applicationName={thisApplicationName}
											applicationID={thisApplicationID}
											platformOwnerEmail={
												thisApplication?.details?.platformOwnerEmail
											}
											applicationOwnerEmail={
												thisApplication?.details?.applicationOwnerEmail
											}
										/>
									)}
								</Container>
							</RequiredPermissions>
						)}

					<Container header={<Header variant="h3">Add-ons</Header>}>
						<ColumnLayout columns={2} variant="text-grid">
							<div>
								<Box variant="awsui-key-label">MongoDB</Box>
								<div>
									{thisApplication.details?.addOns?.mongodb === true
										? 'Yes'
										: 'No'}
								</div>
							</div>
							<div>
								<Box variant="awsui-key-label">Confluent</Box>
								<div>
									{thisApplication.details?.addOns?.confluent === true
										? 'Yes'
										: 'No'}
								</div>
							</div>
						</ColumnLayout>
					</Container>

					{thisApplication.status === 'PENDING_APPROVAL' &&
						CURRENT_USER_ID !== thisApplication.requestorID && (
							<RequiredPermissions
								requiredPermissions={CONSTANTS.APPLICATION_APPROVER}
							>
								<Box float="left">
									<ButtonDropdown
										items={
											isAllTagsApproved
												? [
														{ text: 'To Approve', id: 'to-approve' },
														{ text: 'To Reject', id: 'to-reject' }
												  ]
												: [{ text: 'To Reject', id: 'to-reject' }]
										}
										onItemClick={({ detail }) => {
											if (detail.id === 'to-approve') {
												setShowApprovalForm(true)
												setShowRejectionForm(false)
											}

											if (detail.id === 'to-reject') {
												setShowApprovalForm(false)
												setShowRejectionForm(true)
											}
										}}
										variant="primary"
									>
										Action
									</ButtonDropdown>
								</Box>
							</RequiredPermissions>
						)}
					{showApprovalForm && (
						<ApprovalForm
							thisApplicationID={thisApplicationID}
							shouldShowApprovalForm={setShowApprovalForm}
						/>
					)}

					{showRejectionForm && (
						<RejectionForm
							thisApplicationID={thisApplicationID}
							shouldShowRejectionForm={setShowRejectionForm}
						/>
					)}

					{thisApplication.status === 'APPROVED' && (
						<RequiredPermissions
							requiredPermissions={CONSTANTS.APPLICATION_APPROVER}
						>
							<ApprovalDetails approvalDetails={thisApplication!} />
						</RequiredPermissions>
					)}

					{thisApplication.status === 'APPROVED' &&
						CURRENT_USER_ID !== thisApplication.requestorID &&
						showButton && (
							<RequiredPermissions
								requiredPermissions={CONSTANTS.APPLICATION_APPROVER}
							>
								<Button variant="primary" onClick={handleShowAccessDetails}>
									View Access Details
              </Button>
							</RequiredPermissions>
						)}

					{!showButton && (
						<AccessDetails
							env={thisApplication?.details.environment?.value || thisApplication?.details.environment}
							platformName={thisApplication?.details.platformName?.label || thisApplication?.details.platformName}
							appName={thisApplication?.details.applicationName}
						/>
          )}
				</SpaceBetween>

				<SpaceBetween direction="vertical" size="l">
					<Container header={<Header variant="h3">Comment History</Header>}>
						<CommentsOverview comments={thisApplication.comments} />
					</Container>
          {thisApplication.status === 'APPROVED' && (
            <>
              <ProjectResourcePanel resources={projectResourcePanelItems} />
              <Container header={<Header variant='h3'>Application Resources</Header>}>
                <Button variant='link' onClick={() => setIsCodeBrokkrModalOpen(true)}>
                  Provision Application Repository with Code Brokkr
                </Button>
              </Container>
            </>
					)}
				</SpaceBetween>
      </Grid>

      <CodeBrokkrModal isOpen={isCodeBrokkrModalOpen} setIsOpen={setIsCodeBrokkrModalOpen} />
		</ContentLayout>
	)
}

const getKeyValueApplication = (application: any) => {
	console.log(application, 'application')
	const baseEntries = [
		{
			label: 'Infrastructure Type',
			// Smart fallback: If it's explicitly non-gke, show Non-GKE. Otherwise, assume GKE.
			value: application?.details?.infrastructureType === 'non-gke' ? 'Non-GKE' : 'GKE',
			version: getVersionStr(application?.version, 'infrastructureType')
		},
		{
			label: 'Requestor',
			value: application?.requestorID,
			version: getVersionStr(application?.version, 'applicationName')
		},
		...(application?.details?.infrastructureType === "gke" ?
			[
				{
					label: "GKE Workload Cluster",
					value: application?.wkldClusterName,
					version: getVersionStr(application?.version, 'wkldClusterName')
				}
			] : []
		),
		{
			label: 'Platform Name',
			value: application?.details.platformName?.label || application?.details.platformName,
			version: getVersionStr(application?.version, 'platformName')
		},
		{
			label: 'Application Name',
			value: application?.details.applicationName,
			version: getVersionStr(application?.version, 'applicationName')
		},
		{
			label: 'Environment',
			value: application?.details.environment,
			version: getVersionStr(application?.version, 'environment')
		},
		{
			label: 'Region',
			value: 'Singapore (asia-southeast1)',
			version: getVersionStr(application?.version, 'environment')
		},
		{
			label: 'Application Viewers',
			value: application.details.applicationViewer.join(', '),
			version: getVersionStr(application?.version, 'environment')
		},
		{
			label: 'Application Owner Name',
			value: application?.details?.applicationOwner || application?.details?.applicationOwnerName || 'N/A',
			version: getVersionStr(application?.version, 'environment')
		},
		{
			label: 'Application Owner Email',
			value: application?.details?.applicationOwnerEmail || 'N/A',
			version: getVersionStr(application?.version, 'environment')
		},
		{
			label: 'Platform Owner Name',
			value: application?.details?.platformOwner || 'N/A',
			version: getVersionStr(application?.version, 'environment')
		},
		{
			label: 'Platform Owner Email',
			value: application?.details?.platformOwnerEmail || 'N/A',
			version: getVersionStr(application?.version, 'environment')
		},
		// {
		// 	label: 'Platform Cluster Name',
		// 	value: application.wkldClusterName || 'dummy',
		// 	version: getVersionStr(application?.version, 'environment')
		// },
		{
			label: 'Application Alias',
			value: application.details.applicationAlias,
			version: getVersionStr(application?.version, 'environment')
		},
		{
			label: 'Deployment',
			value: String(application.details.deployment).toUpperCase(),
			version: getVersionStr(application?.version, 'environment')
		},
		{
			label: 'Data Classification',
			value: String(application.details.dataClassification).toUpperCase(),
			version: getVersionStr(application?.version, 'environment')
		},
		{
			label: 'Portfolio',
			value: application.details.group.label,
			version: getVersionStr(application?.version, 'environment')
		},
		{
			label: 'Tier Level',
			value: application.details.tierLevel,
			version: getVersionStr(application?.version, 'environment')
		},
		{
			label: 'WBS Code',
			value: application.details.wbsCode,
			version: getVersionStr(application?.version, 'environment')
		},
		{
			label: 'Cost Center',
			value: application.details.costCenter,
			version: getVersionStr(application?.version, 'environment')
		}
	]

	const additionalEntries = application?.details.additionalEnvName
		? [
				{
					label: 'Source Env Name',
					value: application.details.sourceEnvName,
					version: getVersionStr(application?.version, 'environment')
				},
				{
					label: 'Additional Env Name',
					value: application.details.additionalEnvName,
					version: getVersionStr(application?.version, 'environment')
				}
		  ]
		: []

	return [...baseEntries, ...additionalEntries]
}

const getVersionStr = (version: Array<any> | undefined, key: string) => {
	let versionItems: string[] = []
	version?.forEach((element) => {
		if (!element[key]) return
		if (key === 'region' && element.region) {
			versionItems.unshift(`v${element.versionID}: ${element.region[0].label}`)
		} else if (key === 'platformName' && element.platformName) {
			versionItems.unshift(
				`v${element.versionID}: ${element.platformName.value}`
			)
		} else {
			versionItems.unshift(`v${element.versionID}: ${element[key]}`)
		}
	})
	return versionItems
}

export default ApplicationDetails
